import { useModal } from '@ebay/nice-modal-react';
import { useEffect, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useEnforceSecurityCode from 'hooks/useEnforceSecurityCode';
import CreateSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/CreateSecurityCodeModal/CreateSecurityCodeModal';

/**
 * True only when enforcement is on, the device is verified, and no security code exists.
 * Uses `hasSecurityCode === false` (not `!== true`) so we never trigger during the
 * initial loading phase when the value is still `null`.
 */
const useShouldPromptEnforcedSecurityCode = (): boolean => {
  const enforce = useEnforceSecurityCode();
  const { hasSecurityCode, crossSigningStatus } = useMatrixDeviceContext();
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });

  return (
    enforce && !!matrixClient && crossSigningStatus === CrossSigningStatusEnum.VERIFIED && hasSecurityCode === false
  );
};

/**
 * Safety-net for page reloads only.
 *
 * Primary call sites (ResetKeysModal, useBootstrapCrossSigning) show the
 * CreateSecurityCodeModal directly with the correct enforced props.
 * This gate handles the case where a user reloads the page while the
 * condition (enforced + verified + no security code) is already true.
 *
 * The gate tracks whether `hasSecurityCode` was ever `true` during the
 * current session. If it was, and then flips to `false`, that means a
 * remote key reset happened — `crossSigningStatus` may still read
 * `VERIFIED` due to stale SDK state (localVerified / signedByOwner
 * heuristic in useMatrixDeviceInfo). In that case the gate stays silent
 * and lets VerificationMethodSelectionManager show the verification
 * chooser instead.
 */
const EnforceSecurityCodeGate = () => {
  const shouldShow = useShouldPromptEnforcedSecurityCode();
  const modal = useModal(CreateSecurityCodeModal);
  const { refreshHasSecurityCode, hasSecurityCode } = useMatrixDeviceContext();

  const hadSecurityCode = useRef(false);
  if (hasSecurityCode === true) hadSecurityCode.current = true;

  useEffect(() => {
    if (!shouldShow || modal.visible || hadSecurityCode.current) return;
    modal.show({ enforced: true, showSetupDescription: true, onSuccess: refreshHasSecurityCode });
  }, [shouldShow, modal, refreshHasSecurityCode]);

  return null;
};

export default EnforceSecurityCodeGate;
